import { useState } from "react";
import { PDFDocument } from "pdf-lib";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileDown, FileText, Loader2, Tag, UploadCloud } from "lucide-react";

const A4_PAISAGEM_LARGURA = 841.89;
const A4_PAISAGEM_ALTURA = 595.276;
const TOLERANCIA_PONTOS = 3;
const ETIQUETA_LARGURA = 253;
const ETIQUETA_ALTURA = 421;
const ETIQUETA_Y_INFERIOR = 144;
const ETIQUETAS_X_INICIAL = [30, 295, 560];
const PAGINA_10X15_LARGURA = 100 * 72 / 25.4;
const PAGINA_10X15_ALTURA = 150 * 72 / 25.4;

export default function TratamentoEtiquetaVolumeFull() {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [quantidadeEtiquetas, setQuantidadeEtiquetas] = useState(0);
  const [processando, setProcessando] = useState(false);

  async function selecionarArquivo(file?: File) {
    setArquivo(null);
    setQuantidadeEtiquetas(0);

    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Selecione um arquivo PDF.");
      return;
    }

    try {
      const origem = await PDFDocument.load(await file.arrayBuffer());
      const paginas = origem.getPages();

      if (paginas.length === 0) {
        throw new Error("O PDF não possui páginas.");
      }

      const formatoInvalido = paginas.some((pagina) => {
        const { width, height } = pagina.getSize();
        return Math.abs(width - A4_PAISAGEM_LARGURA) > TOLERANCIA_PONTOS ||
          Math.abs(height - A4_PAISAGEM_ALTURA) > TOLERANCIA_PONTOS;
      });

      if (formatoInvalido) {
        throw new Error("O PDF precisa estar no formato Full A4 paisagem com três etiquetas por página.");
      }

      setArquivo(file);
      setQuantidadeEtiquetas(paginas.length * ETIQUETAS_X_INICIAL.length);
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível ler o PDF.");
    }
  }

  async function gerarPdf10x15() {
    if (!arquivo) return;

    setProcessando(true);
    try {
      const origem = await PDFDocument.load(await arquivo.arrayBuffer());
      const destino = await PDFDocument.create();

      for (const paginaOrigem of origem.getPages()) {
        for (const xInicial of ETIQUETAS_X_INICIAL) {
          const etiqueta = await destino.embedPage(paginaOrigem, {
            left: xInicial,
            right: xInicial + ETIQUETA_LARGURA,
            bottom: ETIQUETA_Y_INFERIOR,
            top: ETIQUETA_Y_INFERIOR + ETIQUETA_ALTURA,
          });
          const paginaDestino = destino.addPage([PAGINA_10X15_LARGURA, PAGINA_10X15_ALTURA]);
          const escala = Math.min(
            PAGINA_10X15_LARGURA / etiqueta.width,
            PAGINA_10X15_ALTURA / etiqueta.height,
          );
          const largura = etiqueta.width * escala;
          const altura = etiqueta.height * escala;

          paginaDestino.drawPage(etiqueta, {
            x: (PAGINA_10X15_LARGURA - largura) / 2,
            y: (PAGINA_10X15_ALTURA - altura) / 2,
            width: largura,
            height: altura,
          });
        }
      }

      const pdf = await destino.save();
      const url = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${arquivo.name.replace(/\.pdf$/i, "")}-10x15.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`PDF gerado com ${quantidadeEtiquetas} etiqueta(s) em 10×15 cm.`);
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível gerar o PDF de etiquetas.");
    } finally {
      setProcessando(false);
    }
  }

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Tag className="w-6 h-6" />
          Tratamento de Etiqueta de Volume Full
        </h1>
        <p className="text-muted-foreground text-sm">
          Converte o PDF Full A4 em etiquetas individuais de 10×15 cm.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>PDF de etiquetas Full</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pdf-etiquetas-full">Arquivo PDF</Label>
              <Input
                id="pdf-etiquetas-full"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => void selecionarArquivo(event.target.files?.[0])}
                disabled={processando}
              />
              <p className="text-xs text-muted-foreground">
                Formato aceito: PDF Full A4 paisagem, com três etiquetas por página.
              </p>
            </div>

            {arquivo && (
              <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
                <div className="flex items-center gap-2 font-medium">
                  <FileText className="w-4 h-4" />
                  <span className="truncate">{arquivo.name}</span>
                </div>
                <p className="text-muted-foreground">
                  {quantidadeEtiquetas} etiqueta(s) serão geradas em {quantidadeEtiquetas} página(s) 10×15 cm.
                </p>
              </div>
            )}

            <Button className="w-full" onClick={gerarPdf10x15} disabled={!arquivo || processando}>
              {processando ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Gerando PDF...
                </>
              ) : (
                <>
                  <FileDown className="w-4 h-4 mr-2" />
                  Gerar PDF 10×15
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Resultado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <UploadCloud className="w-4 h-4 mt-0.5 shrink-0" />
              <p>O PDF é processado localmente no navegador e não é salvo no sistema.</p>
            </div>
            <p>Cada página A4 de origem é separada em três etiquetas, mantendo a ordem original.</p>
            <p>O arquivo final contém uma etiqueta por página, no formato vertical de 10×15 cm.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
